import React, { useState, useEffect } from 'react';
import { clientAPI } from '../api/api';
import { FileText, Search, Shield } from 'lucide-react';
import { formatDate } from '../utils/dateFormat';

const FileRegistry = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDSC, setFilterDSC] = useState('all'); // all, has_dsc, no_dsc

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await clientAPI.getAll();
      setClients(response.data);
    } catch (error) {
      console.error('Failed to fetch clients:', error);
    } finally {
      setLoading(false);
    }
  };

  // Count total DSCs across all clients
  const totalDSCCount = clients.reduce((sum, c) => sum + (c.digital_signatures?.length || 0), 0);
  const clientsWithDSC = clients.filter(c => c.digital_signatures?.length > 0).length;

  // Filter clients
  const filteredClients = clients.filter(client => {
    // Search filter - includes phone and DSC holder names
    const search = searchTerm.toLowerCase();
    
    // Check DSC holder names (now supports multiple)
    const dscHolderMatch = client.digital_signatures?.some(dsc => 
      dsc.holder_name?.toLowerCase().includes(search)
    );
    
    const matchesSearch = 
      !searchTerm ||
      client.name?.toLowerCase().includes(search) ||
      client.client_code?.toLowerCase().includes(search) ||
      client.file_number?.toLowerCase().includes(search) ||
      client.pan?.toLowerCase().includes(search) ||
      client.phone?.includes(search) ||
      dscHolderMatch;
    
    if (!matchesSearch) return false;

    // DSC filter (updated for multiple DSCs)
    if (filterDSC === 'has_dsc') return client.digital_signatures?.length > 0;
    if (filterDSC === 'no_dsc') return !client.digital_signatures?.length;

    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">File Registry</h2>
        <p className="text-gray-600">Client file numbers and digital signature management</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <FileText className="w-8 h-8 text-blue-600 mr-3" />
            <div>
              <p className="text-sm text-gray-500">Total Clients</p>
              <p className="text-xl font-bold">{clients.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <Shield className="w-8 h-8 text-green-600 mr-3" />
            <div>
              <p className="text-sm text-gray-500">Clients with DSC</p>
              <p className="text-xl font-bold text-green-600">{clientsWithDSC}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow border">
          <div className="flex items-center">
            <Shield className="w-8 h-8 text-purple-600 mr-3" />
            <div>
              <p className="text-sm text-gray-500">Total DSCs</p>
              <p className="text-xl font-bold text-purple-600">{totalDSCCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search by name, code, file number, phone, DSC holder..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border rounded-lg"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">DSC Filter</label>
            <select
              value={filterDSC}
              onChange={(e) => setFilterDSC(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="all">All Clients</option>
              <option value="has_dsc">With DSC</option>
              <option value="no_dsc">Without DSC</option>
            </select>
          </div>
        </div>
      </div>

      {/* Clients Table */}
      <div className="bg-white rounded-lg shadow border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">File Number</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">PAN</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Digital Signatures</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredClients.map(client => (
                <tr key={client.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium">{client.name}</div>
                    {client.contact_person && (
                      <div className="text-xs text-gray-500">{client.contact_person}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">{client.client_code || '-'}</td>
                  <td className="px-4 py-3 text-sm">{client.phone || '-'}</td>
                  <td className="px-4 py-3">
                    {client.file_number ? (
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm font-medium">
                        {client.file_number}
                      </span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono">{client.pan || '-'}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 text-xs rounded bg-gray-100 text-gray-700">
                      {client.category}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {client.digital_signatures?.length > 0 ? (
                      <div className="space-y-1">
                        {client.digital_signatures.map((dsc, idx) => (
                          <div key={idx} className="flex items-center space-x-2">
                            <span className={`w-3 h-3 rounded-full flex-shrink-0 ${
                              dsc.tag_color === 'red' ? 'bg-red-500' :
                              dsc.tag_color === 'yellow' ? 'bg-yellow-500' :
                              dsc.tag_color === 'blue' ? 'bg-blue-500' :
                              dsc.tag_color === 'white' ? 'bg-gray-200 border border-gray-400' :
                              'bg-green-500'
                            }`}></span>
                            <span className="text-sm font-medium">{dsc.holder_name}</span>
                            {dsc.expiry_date && (
                              <span className="text-xs text-gray-500">
                                ({formatDate(dsc.expiry_date)})
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                </tr>
              ))}
              {filteredClients.length === 0 && (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>No clients found matching your criteria.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FileRegistry;
